import type { TargetingTermValue } from '../../models';

const NAME_OF_COUNTRY_DIMENSION = 'country';
const NAME_OF_PUBLISHER_DIMENSION = 'publisher';

function dedupeStringsPreserveOrder(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    if (!seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

// Dimension ids on targeting term values always arrive lowercase from the server, but a
// couple of legacy/test paths send uppercase, so match case-insensitively to match the pre-
// refactor behavior of writeAdToCache.
const isCountryDimension = (term: TargetingTermValue): boolean =>
  term.dimension?.id?.toLowerCase() === NAME_OF_COUNTRY_DIMENSION;

const isPublisherDimension = (term: TargetingTermValue): boolean =>
  term.dimension?.id?.toLowerCase() === NAME_OF_PUBLISHER_DIMENSION;

/**
 * Extracts publisher names from positive (`include !== false`) publisher dimension rows.
 */
export function getPublishersFromTargetingTermValues(termValues: TargetingTermValue[]): string[] {
  return (termValues ?? []).filter(isPublisherDimension).map((term) => term.value.name);
}

/** Country codes (`value.id`) from country dimension rows with `include !== false` (positive / default-include). */
export function getCountryCodesFromTargetingTermValues(termValues: TargetingTermValue[]): string[] {
  const codes: string[] = [];
  for (const term of termValues ?? []) {
    if (isCountryDimension(term) && term.value?.id && term.include !== false) {
      codes.push(term.value.id);
    }
  }
  return codes;
}

/** Country codes excluded by the ad when `include === false` (GraphQL / UI "not" on the country dimension). */
export function getExcludedCountryCodesFromTargetingTermValues(termValues: TargetingTermValue[]): string[] {
  const codes: string[] = [];
  for (const term of termValues ?? []) {
    if (isCountryDimension(term) && term.value?.id && term.include === false) {
      codes.push(term.value.id);
    }
  }
  return codes;
}

/** Every country code referenced on a country dimension row (include true or false), for invalid-country checks. */
export function getAllCountryCodesMentionedInAdTargeting(termValues: TargetingTermValue[]): string[] {
  const codes: string[] = [];
  for (const term of termValues ?? []) {
    if (isCountryDimension(term) && term.value?.id) {
      codes.push(term.value.id);
    }
  }
  return dedupeStringsPreserveOrder(codes);
}

/**
 * `countriesTarget` for the ad from the submitted targeting rows.
 * - With country rows where `include === false` ("not"): line item countries minus those excluded codes (order preserved).
 * - Otherwise: unique country codes from positive (`include !== false`) country dimension rows; if the ad mentions no
 *   countries that way, use the same list as the line item (`lineItemCountries`).
 */
export function getCountriesTargetFromAdTargetingSubmission(
  termValues: TargetingTermValue[],
  lineItemCountries: string[] | undefined
): string[] {
  const excluded = dedupeStringsPreserveOrder(getExcludedCountryCodesFromTargetingTermValues(termValues));
  if (excluded.length > 0) {
    const exclusionSet = new Set(excluded);
    return (lineItemCountries ?? []).filter((c) => !exclusionSet.has(c));
  }
  const positive = dedupeStringsPreserveOrder(getCountryCodesFromTargetingTermValues(termValues));
  if (positive.length > 0) {
    return positive;
  }
  return [...(lineItemCountries ?? [])];
}

/**
 * `displayPublisherTarget` after save: ad-selected publisher names, else the line item list when the ad has no
 * publisher rows. If the line item list is empty (e.g. not yet loaded in cache), keep the ad's current value so we
 * do not wipe a good CampaignSearcher-derived list when only patching invalid flags.
 */
export function getDisplayPublisherTargetAfterAdTargetingSave(
  submittedAdTargetingTermValues: TargetingTermValue[],
  lineItemDisplayPublisherTarget: string[],
  serverAdDisplayPublisherTarget: string[] = []
): string[] {
  const fromAd = getPublishersFromTargetingTermValues(submittedAdTargetingTermValues);
  if (fromAd.length > 0) {
    return fromAd;
  }
  if (lineItemDisplayPublisherTarget.length > 0) {
    return [...lineItemDisplayPublisherTarget];
  }
  return [...serverAdDisplayPublisherTarget];
}

/**
 * True when the ad explicitly targets at least one publisher not included on the line item.
 */
export function computeHasInvalidPublisherForAdSave(
  submittedAdTargetingTermValues: TargetingTermValue[],
  lineItemDisplayPublisherTarget: string[]
): boolean {
  const fromAd = getPublishersFromTargetingTermValues(submittedAdTargetingTermValues);
  if (fromAd.length === 0) {
    return false;
  }
  const allowed = new Set(lineItemDisplayPublisherTarget);
  return fromAd.some((p) => !allowed.has(p));
}

/**
 * True when the ad's targeting mentions any country code not present on the line item's countries list.
 * `adCountryCodes` must list every country `value.id` on country dimension rows (include true or false).
 * If the line item's countries list is empty or null, any mentioned ad country yields invalid.
 */
export function computeHasInvalidCountryForAdSave(
  adCountryCodes: string[],
  lineItemCountries: string[] | undefined
): boolean {
  if (adCountryCodes.length === 0) {
    return false;
  }
  const allowed = lineItemCountries ?? [];
  if (allowed.length === 0) {
    return true;
  }
  const set = new Set(allowed);
  return adCountryCodes.some((c) => !set.has(c));
}
